import { Outlet, RouteObject, useRoutes } from 'react-router-dom';
import { FC, lazy, Suspense } from 'react';
import { HomePage } from '../pages/home-page/HomePage';
import { ProtectedRoute } from '../auth/protected-route/ProtectedRoute';
import { UserRole } from '../model/Types';
import { ErrorPage } from '../pages/error-page/ErrorPage';
import { SUPPORTED_LANG_CODES } from '../config/languages';
import { BLOG_WHEN_TO_SEEK_HELP_LEGACY_SLUGS, BLOG_WHEN_TO_SEEK_HELP_SLUG } from '../pages/blog/when-to-seek-help/blogWhenToSeekHelpConfig';
import { BLOG_FIRST_SESSION_SLUG } from '../pages/blog/first-psychotherapy-session/blogFirstSessionConfig';
import { useLocaleSync } from '../hooks/useLocaleSync';

const TherapistsPage = lazy(() => import('../pages/therapists-page/TherapistsPage').then(m => ({ default: m.TherapistsPage })));
const TherapistInfoPage = lazy(() => import('../pages/therapist-info-page/TherapistInfoPage').then(m => ({ default: m.TherapistInfoPage })));
const TherapistFormPage = lazy(() => import('../pages/therapist-form-page/TherapistFormPage').then(m => ({ default: m.TherapistFormPage })));
const ForTherapistsPage = lazy(() => import('../pages/for-therapists/ForTherapistsPage').then(m => ({ default: m.ForTherapistsPage })));
const WorkshopsPage = lazy(() => import('../pages/workshops-page/WorkshopsPage').then(m => ({ default: m.WorkshopsPage })));
const LogInPage = lazy(() => import('../pages/login-page/LogInPage').then(m => ({ default: m.LogInPage })));
const SignUpPage = lazy(() => import('../pages/signup-page/SignupPage').then(m => ({ default: m.SignUpPage })));
const ForgotPasswordPage = lazy(() => import('../pages/forgot-password-page/ForgotPasswordPage').then(m => ({ default: m.ForgotPasswordPage })));
const ResetPasswordPage = lazy(() => import('../pages/reset-password-page/ResetPasswordPage').then(m => ({ default: m.ResetPasswordPage })));
const ProfilePage = lazy(() => import('../pages/profile-page/ProfilePage').then(m => ({ default: m.ProfilePage })));
const HistoryPage = lazy(() => import('../pages/history-page/HistoryPage').then(m => ({ default: m.HistoryPage })));
const ScheduleStep1Page = lazy(() => import('../pages/schedule-page/step-1/ScheduleStep1Page').then(m => ({ default: m.ScheduleStep1Page })));
const ScheduleStep2Page = lazy(() => import('../pages/schedule-page/step-2/ScheduleStep2Page').then(m => ({ default: m.ScheduleStep2Page })));
const ScheduleStep3Page = lazy(() => import('../pages/schedule-page/step-3/ScheduleStep3Page').then(m => ({ default: m.ScheduleStep3Page })));
const ScheduleStep4Page = lazy(() => import('../pages/schedule-page/step-4/ScheduleStep4Page').then(m => ({ default: m.ScheduleStep4Page })));
const ScheduleStep5Page = lazy(() =>
    import('../pages/schedule-page/step-5/ScheduleStep5Page').then(m => ({ default: m.ScheduleStep5Page })),
);
const ScheduleLegacyStep6Redirect = lazy(() =>
    import('../pages/schedule-page/ScheduleLegacyStep6Redirect').then(m => ({
        default: m.ScheduleLegacyStep6Redirect,
    })),
);
const ScheduleStep3GuestPage = lazy(() => import('../pages/schedule-page/step-3-guest/ScheduleStep3GuestPage').then(m => ({ default: m.ScheduleStep3GuestPage })));
const ScheduleStep4IpsPage = lazy(() => import('../pages/schedule-page/step-4-ips/ScheduleStep4IpsPage').then(m => ({ default: m.ScheduleStep4IpsPage })));
const ScheduleStep5IpsPage = lazy(() => import('../pages/schedule-page/step-5-ips/ScheduleStep5IpsPage').then(m => ({ default: m.ScheduleStep5IpsPage })));
const ScheduleStep6IpsPage = lazy(() => import('../pages/schedule-page/step-6-ips/ScheduleStep6IpsPage').then(m => ({ default: m.ScheduleStep6IpsPage })));
const ScheduleStep5IpsCompletePage = lazy(() => import('../pages/schedule-page/step-5-ips-complete/ScheduleStep5IpsCompletePage').then(m => ({ default: m.ScheduleStep5IpsCompletePage })));
const SubscriptionSchedulePage = lazy(() => import('../pages/schedule-page/subscription/SubscriptionSchedulePage').then(m => ({ default: m.SubscriptionSchedulePage })));
const TermsOfUsePage = lazy(() => import('../pages/terms-of-use-page/TermsOfUsePage').then(m => ({ default: m.TermsOfUsePage })));
const PaymentInstructionsPage = lazy(() => import('../pages/payment-instructions-page/PaymentInstructionsPage').then(m => ({ default: m.PaymentInstructionsPage })));
const SessionsManagementPage = lazy(() => import('../pages/sessions-management-page/SessionsManagementPage').then(m => ({ default: m.SessionsManagementPage })));
const CalendarAvailabilityManagementPage = lazy(() => import('../pages/calendar-availability-management-page/CalendarAvailabilityManagementPage').then(m => ({ default: m.CalendarAvailabilityManagementPage })));
const PatientManagementPage = lazy(() => import('../pages/patient-management-page/PatientManagementPage').then(m => ({ default: m.PatientManagementPage })));
const WorkshopManagementPage = lazy(() => import('../pages/workshop-management-page/WorkshopManagementPage').then(m => ({ default: m.WorkshopManagementPage })));
const BlogPage = lazy(() =>
    import('../pages/blog/BlogPage').then((m) => ({
        default: m.BlogPage,
    })),
);
const BlogWhenToSeekHelpPage = lazy(() =>
    import('../pages/blog/when-to-seek-help/BlogWhenToSeekHelpPage').then((m) => ({
        default: m.BlogWhenToSeekHelpPage,
    })),
);
const BlogWhenToSeekHelpLegacyRedirect = lazy(() =>
    import('../pages/blog/when-to-seek-help/BlogWhenToSeekHelpLegacyRedirect').then((m) => ({
        default: m.BlogWhenToSeekHelpLegacyRedirect,
    })),
);
const BlogFirstSessionPage = lazy(() =>
    import('../pages/blog/first-psychotherapy-session/BlogFirstSessionPage').then((m) => ({
        default: m.BlogFirstSessionPage,
    })),
);
const WellbeingPage = lazy(() => import('../pages/wellbeing-page/WellbeingPage').then(m => ({ default: m.WellbeingPage })));
const AboutPage = lazy(() => import('../pages/about-page/AboutPage').then(m => ({ default: m.AboutPage })));

/**
 * Layout that mounts useLocaleSync so i18n / <html lang> / appLanguage signal
 * always reflect the language segment of the URL.
 */
const LocaleLayout: FC = () => {
    useLocaleSync();
    return <Outlet />;
};

/**
 * Shared app routes. Rendered both at the root (default Serbian) and
 * under each non-default language prefix (/en, /de, /fr, /ru).
 */
const appRoutes: RouteObject[] = [
    { index: true, element: <HomePage /> },
    { path: 'for-therapists', element: <ForTherapistsPage /> },
    { path: 'wellbeing', element: <WellbeingPage /> },
    { path: 'about', element: <AboutPage /> },
    { path: 'terms-of-use', element: <TermsOfUsePage /> },
    { path: 'payment-instructions', element: <PaymentInstructionsPage /> },
    { path: 'trps', element: <TherapistsPage /> },
    { path: 'ws', element: <WorkshopsPage /> },
    { path: 'blog', element: <BlogPage /> },
    { path: BLOG_WHEN_TO_SEEK_HELP_SLUG, element: <BlogWhenToSeekHelpPage /> },
    { path: BLOG_FIRST_SESSION_SLUG, element: <BlogFirstSessionPage /> },
    ...BLOG_WHEN_TO_SEEK_HELP_LEGACY_SLUGS.map((legacySlug) => ({
        path: legacySlug,
        element: <BlogWhenToSeekHelpLegacyRedirect />,
    })),
    { path: 'trp/info/:id/:lang', element: <TherapistInfoPage /> },
    { path: 'trp/user/:lang/:username', element: <TherapistInfoPage /> },
    { path: 'user/login', element: <LogInPage /> },
    { path: 'user/signup', element: <SignUpPage /> },
    { path: 'user/forgot-password', element: <ForgotPasswordPage /> },
    { path: 'user/reset-password', element: <ResetPasswordPage /> },
    {
        path: 'user/profile',
        element: (
            <ProtectedRoute
                allowedRoles={[UserRole.ADMIN, UserRole.THERAPIST, UserRole.USER]}
            >
                <ProfilePage />
            </ProtectedRoute>
        ),
    },
    // Schedule pages
    { path: 'schedule/:therapistId/step-1', element: <ScheduleStep1Page /> },
    { path: 'schedule/:therapistId/step-2', element: <ScheduleStep2Page /> },
    { path: 'schedule/:therapistId/step-3', element: <ScheduleStep3Page /> },
    { path: 'schedule/:therapistId/step-3-guest', element: <ScheduleStep3GuestPage /> },
    { path: 'schedule/:therapistId/step-4', element: <ScheduleStep4Page /> },
    { path: 'schedule/:therapistId/step-5', element: <ScheduleStep5Page /> },
    { path: 'schedule/:therapistId/step-6', element: <ScheduleLegacyStep6Redirect /> },
    // IPS bankovni-transfer tok (alternativa Monri kartici, vidi util/monriEligibility.ts)
    { path: 'schedule/:therapistId/step-4-ips', element: <ScheduleStep4IpsPage /> },
    { path: 'schedule/:therapistId/step-5-ips', element: <ScheduleStep5IpsPage /> },
    { path: 'schedule/:therapistId/step-6-ips', element: <ScheduleStep6IpsPage /> },
    // Završna "Hvala" stranica za korisnike koji su na Step-4 (Monri tok) eksplicitno
    // odabrali IPS opciju (kartica nestabilna). Sesija je vec SELECTED iz Step-3, na
    // Step-4 se samo posalje IPS email i navigira ovde — bez prolaska kroz step-5-ips
    // (uslovi/captcha) i step-6-ips (kratak thank-you).
    { path: 'schedule/:therapistId/step-5-ips-complete', element: <ScheduleStep5IpsCompletePage /> },
    // "Pretplata" tok — kada terapeut ima subscription=true, ovo je odredište
    // umesto step-1. Ne prolazi se kroz izbor terapije/datuma/plaćanje;
    // prikazuje se samo `therapistLanguages[0].note` (uputstvo za direktnu uplatu).
    { path: 'schedule/:therapistId/subscription', element: <SubscriptionSchedulePage /> },
    // Admin pages
    {
        path: 'adm/addTherapist',
        element: (
            <ProtectedRoute allowedRoles={[UserRole.ADMIN]} isAdminOnly={true}>
                <TherapistFormPage />
            </ProtectedRoute>
        ),
    },
    {
        path: 'adm/editTherapist/:id',
        element: (
            <ProtectedRoute allowedRoles={[UserRole.ADMIN]} isAdminOnly={true}>
                <TherapistFormPage />
            </ProtectedRoute>
        ),
    },
    {
        path: 'adm/history',
        element: (
            <ProtectedRoute allowedRoles={[UserRole.ADMIN]} isAdminOnly={true}>
                <HistoryPage />
            </ProtectedRoute>
        ),
    },
    {
        path: 'adm/session/management',
        element: (
            <ProtectedRoute allowedRoles={[UserRole.ADMIN]} isAdminOnly={true}>
                <SessionsManagementPage />
            </ProtectedRoute>
        ),
    },
    {
        path: 'adm/calendar-availability/management',
        element: (
            <ProtectedRoute allowedRoles={[UserRole.ADMIN]} isAdminOnly={true}>
                <CalendarAvailabilityManagementPage />
            </ProtectedRoute>
        ),
    },
    {
        path: 'adm/ws/management',
        element: (
            <ProtectedRoute allowedRoles={[UserRole.ADMIN]} isAdminOnly={true}>
                <WorkshopManagementPage />
            </ProtectedRoute>
        ),
    },
    {
        path: 'adm/patient/management',
        element: (
            <ProtectedRoute allowedRoles={[UserRole.ADMIN]} isAdminOnly={true}>
                <PatientManagementPage />
            </ProtectedRoute>
        ),
    },
    // Error pages
    { path: 'unauthorized', element: <ErrorPage statusCode={401} /> },
    { path: 'forbidden', element: <ErrorPage statusCode={403} /> },
    { path: '*', element: <ErrorPage statusCode={404} /> },
];

const buildRouteTree = (): RouteObject[] => [
    {
        path: '/',
        element: <LocaleLayout />,
        children: appRoutes,
    },
    // Every supported language (including the default 'sr') gets a prefixed
    // variant so that /sr/trps also renders (advertised URLs without prefix
    // keep working because of the root '/' route above).
    ...SUPPORTED_LANG_CODES.map<RouteObject>((lang) => ({
        path: `/${lang}`,
        element: <LocaleLayout />,
        children: appRoutes,
    })),
];

export const BalancePath: FC = () => {
    const element = useRoutes(buildRouteTree());
    return <Suspense fallback={<div style={{ minHeight: '100vh' }} />}>{element}</Suspense>;
};
